import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, Row, Button } from "react-bootstrap";
import {
  boatTypeEnumReversed,
  Directions,
  emptyString,
  feedbackTypes,
  levelEnumReversed,
  modeConstants,
  proficiencyLevelEnumReversed,
  sportConstants,
  studentConstants,
  table,
  TableColumnStyle,
  timezoneConstants,
  translationConstants,
} from "../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { allSharedComponents } from "../../../shared";
import CustomModal from "../../../shared/components/CustomModal/CustomModal";
import {
  antSwitchStyle,
  changeTableSortIcons,
  debounce,
  getSortDirectionProperty,
  getLevelAndProficiencyLevel,
  removeSpace,
  serverFormatToDateString,
} from "../../../system";
import CourseModal from "../../../shared/components/CourseModal/CourseModal";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import { CourseActions } from "../../../redux/actions/course.action";
import FeedbackModal from "../../../shared/components/FeedbackModal/FeedbackModal";
import cancelIcon from "../../../assets/images/icon/crossIcon.svg";

const { DataTable, DeleteModal } = allSharedComponents;

const initialGridState = {
  length: 10,
  pageNumber: 1,
  orderBy: null,
  filterQuery: null,
  search: null,
};

const AntSwitch = styled(Switch)(({ theme }) => antSwitchStyle(theme));

const Courses = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const modalOpen = useSelector((state) => state.UserReducer.openModal);
  const usersData = useSelector((state) => state.CourseReducer.systemCourses);

  const systemUserId = useSelector(
    (state) => state.LoginReducer.UserData.user.id
  );

  const direction = useSelector((state) => {
    return state.LanguageChangeReducer.direction;
  });

  const sortingInProgressRef = useRef(false);
  const [rowData, setRowData] = useState("");
  const [gridState, setGridState] = useState(initialGridState);
  const [defaultPage, setDefaultPage] = useState(gridState.pageNumber);
  const [courseId, setCourseId] = useState();
  const [mode, setMode] = useState(modeConstants.ADD_MODE);
  const [inputSearch, setInputSearch] = useState(emptyString);
  const searchInputRef = useRef(null);

  const getRowData = (data) => {
    dispatch(CourseActions.getStudentById(data.id));
    setIsShow(true);
  };

  const handleFeedbackModal = (data) => {
    setCourseId(data.id);
    dispatch(CourseActions.getQuestions());
    setIsShowOpenFeedback(true);
  };

  const [isShow, setIsShow] = useState(false);
  const [isShowOpenFeedback, setIsShowOpenFeedback] = useState(false);

  const onSetShow = () => {
    setIsShow((prev) => !prev);
  };

  const onSetFeedbackShow = () => {
    setIsShowOpenFeedback((prev) => !prev);
  };

  const getcolumns = () => [
    {
      id: "sportId",
      name: t(translationConstants.SPORT),
      selector: (row) =>
        `${
          row.sportId === sportConstants.SAILING
            ? t(translationConstants.SAILING)
            : row.sportId === sportConstants.KITESURFING
            ? t(translationConstants.KITE_SURFING)
            : row.sportId === sportConstants.WINDSURFING
            ? t(translationConstants.WIND_SURFING)
            : row.sportId === sportConstants.WINGFOILING
            ? t(translationConstants.WING_FOILING)
            : ""
        }`,
      reorder: true,
      sortable: true,
      sortDirection: null,
      width: TableColumnStyle.COLUMN_WIDTH_120,
    },
    {
      id: "boatTypeId",
      name: t(translationConstants.BOAT_TYPE),
      selector: (row) =>
        t(translationConstants[boatTypeEnumReversed[row.boatTypeId]]),
      reorder: true,
      sortable: true,
      sortDirection: null,
      width: TableColumnStyle.COLUMN_WIDTH_140,
    },
    {
      id: "instructorName",
      name: t(translationConstants.INSTRUCTOR_NAME),
      selector: (row) =>
        Directions.rtl === direction
          ? removeSpace(row.instructorArabicName)
            ? row.instructorArabicName
            : row.instructorName
            ? row.instructorName
            : "--"
          : row.instructorName
          ? row.instructorName
          : "--",
      reorder: true,
      sortable: true,
      sortDirection: null,
      width: table.COLUMN_WIDTH,
    },
    {
      id: "centerName",
      name: t(translationConstants.CENTER),
      selector: (row) =>
        Directions.rtl === direction
          ? removeSpace(row.centerArabicName)
            ? row.centerArabicName
            : row.centerName
            ? row.centerName
            : "--"
          : row.centerName
          ? row.centerName
          : "--",
      reorder: true,
      sortable: true,
      sortDirection: null,
      width: table.COLUMN_WIDTH,
    },
    {
      id: "addedDate",
      name: t(translationConstants.DATE_ADDED),
      selector: (row) =>
        `${serverFormatToDateString(
          row.addedDateString,
          timezoneConstants.dayMonthYearFormat
        )}`,
      reorder: true,
      sortable: true,
      sortDirection: null,
      width: table.COLUMN_WIDTH,
    },
    {
      id: "noOfHours",
      name: t(translationConstants.noOfHours),
      selector: (row) => row.noOfHours,
      reorder: true,
      sortable: true,
      sortDirection: null,
      width: table.COLUMN_WIDTH,
    },
    {
      id: "isLevelCompleted",
      name: t(translationConstants.LEVEL_COMPLETED),
      selector: (row) =>
        row.isLevelCompleted
          ? t(translationConstants.YES)
          : t(translationConstants.NO),
      reorder: true,
      sortable: true,
      sortDirection: null,
      width: table.COLUMN_WIDTH,
    },
    {
      id: "levelId",
      name: t(translationConstants.LEVEL_ACHIEVED),
      selector: (row) =>
        row.levelId
          ? getLevelAndProficiencyLevel(
              t,
              row.levelId,
              row.proficiencyLevelId
            )
          : row.isLevelCompleted && row.levelId === null
          ? t(translationConstants.GENERAL_TRAINING)
          : "--",
      reorder: true,
      sortable: true,
      sortDirection: null,
      width: TableColumnStyle.COLUMN_WIDTH_180,
    },

    {
      id: "actions",
      name: (
        <div className="action-column">{t(translationConstants.Actions)}</div>
      ),
      cell: (row) => (
        <>
          <div className="action-column gap-1">
            <svg
              onClick={() => {
                getRowData(row);
                setMode(modeConstants.VIEW_MODE);
              }}
              width="20"
              height="20"
              viewBox={direction === Directions.rtl ? "0 0 20 16" : "0 0 20 16"}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.1685 8.99922C16.1685 8.99922 14.0341 13.268 9.05391 13.268C4.07371 13.268 1.93933 8.99922 1.93933 8.99922C1.93933 8.99922 4.07371 4.73047 9.05391 4.73047C14.0341 4.73047 16.1685 8.99922 16.1685 8.99922Z"
                stroke="#588930"
                strokeWidth="1.42292"
                strokeLinecap="round"
              />
              <circle
                cx="9.05381"
                cy="8.99766"
                r="2.13438"
                stroke="#588930"
                strokeWidth="1.42292"
                strokeLinecap="round"
              />
            </svg>
            {feedbackTypes.FEED_BACK_TYPE_NOT_GIVEN === row.feedbackStatus ? (
              <svg
                width="16"
                height="14"
                viewBox="-1 -1 16 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => {
                  handleFeedbackModal(row);
                  setMode(modeConstants.ADD_MODE);
                }}
              >
                <path
                  d="M10.559 4.54342L8.68276 4.27585L7.84377 2.60733C7.82095 2.56192 7.78565 2.52369 7.74184 2.49696C7.69803 2.47022 7.64748 2.45605 7.59589 2.45605C7.54431 2.45605 7.49375 2.47022 7.44995 2.49696C7.40614 2.52369 7.37083 2.56192 7.34802 2.60733L6.50876 4.27585L4.6325 4.54342C4.58146 4.55067 4.53351 4.57179 4.49406 4.6044C4.45462 4.637 4.42526 4.67979 4.40931 4.72792C4.39335 4.77605 4.39144 4.8276 4.40379 4.87674C4.41614 4.92588 4.44225 4.97065 4.47917 5.00598L5.83702 6.30493L5.51646 8.13903C5.50777 8.18892 5.51348 8.24019 5.53293 8.28707C5.55238 8.33394 5.5848 8.37454 5.62653 8.40428C5.66826 8.43402 5.71763 8.45171 5.76907 8.45535C5.8205 8.45899 5.87195 8.44844 5.9176 8.4249L7.59583 7.55911L9.27407 8.4249C9.31972 8.44844 9.37117 8.45899 9.4226 8.45535C9.47404 8.45171 9.52341 8.43402 9.56514 8.40428C9.60687 8.37454 9.63929 8.33394 9.65874 8.28707C9.67819 8.24019 9.68389 8.18892 9.67521 8.13903L9.35465 6.30493L10.7123 5.00598C10.7493 4.97065 10.7754 4.92588 10.7877 4.87674C10.8001 4.8276 10.7982 4.77605 10.7822 4.72792C10.7662 4.67979 10.7369 4.637 10.6974 4.6044C10.658 4.57179 10.6101 4.55067 10.559 4.54342ZM8.86457 6.01617C8.83253 6.04688 8.80855 6.08476 8.7947 6.12657C8.78085 6.16838 8.77755 6.21286 8.78508 6.25619L9.03558 7.68892L7.72445 7.01255C7.68483 6.99194 7.64067 6.98116 7.59582 6.98116C7.55098 6.98116 7.50682 6.99194 7.4672 7.01255L6.15608 7.68892L6.40645 6.25618C6.41397 6.21285 6.41067 6.16837 6.39682 6.12656C6.38298 6.08475 6.359 6.04687 6.32695 6.01616L5.2662 5.00122L6.732 4.79219C6.77636 4.78589 6.81848 4.76909 6.85474 4.74324C6.891 4.71739 6.92031 4.68327 6.94013 4.64383L7.59583 3.34038L8.25125 4.64383C8.2711 4.68328 8.30043 4.7174 8.33671 4.74325C8.373 4.76909 8.41514 4.78589 8.45951 4.79219L9.92531 5.00122L8.86457 6.01617Z"
                  fill="#9DC540"
                  stroke="#9DC540"
                  stroke-width="0.5"
                />
                <path
                  d="M13.0826 0.856445H2.50897C2.05476 0.856963 1.6193 1.04107 1.29812 1.36839C0.976944 1.6957 0.796284 2.13949 0.795776 2.60238V9.01671C0.796284 9.4796 0.976944 9.92338 1.29812 10.2507C1.6193 10.578 2.05476 10.7621 2.50897 10.7626H5.52072V13.3821C5.52072 13.4363 5.5365 13.4894 5.56608 13.5345C5.59567 13.5796 5.63771 13.6148 5.68691 13.6356C5.73611 13.6563 5.79024 13.6618 5.84247 13.6512C5.89469 13.6406 5.94266 13.6144 5.98031 13.5761L8.74071 10.7626H13.0826C13.5368 10.7621 13.9723 10.578 14.2934 10.2507C14.6146 9.92338 14.7953 9.4796 14.7958 9.01671V2.60238C14.7953 2.13949 14.6146 1.6957 14.2934 1.36839C13.9723 1.04108 13.5368 0.856964 13.0826 0.856445ZM14.2573 9.01671C14.257 9.33411 14.1331 9.63841 13.9129 9.86285C13.6926 10.0873 13.394 10.2135 13.0826 10.2139H8.62923C8.55784 10.2139 8.48937 10.2428 8.43888 10.2943L6.05918 12.7197V10.4883C6.05919 10.4522 6.05224 10.4165 6.03871 10.3833C6.02518 10.35 6.00535 10.3197 5.98035 10.2942C5.95535 10.2687 5.92566 10.2485 5.89299 10.2348C5.86033 10.221 5.82531 10.2139 5.78995 10.2139H2.50897C2.19752 10.2135 1.89892 10.0873 1.67869 9.86285C1.45846 9.63841 1.33458 9.33411 1.33424 9.01671V2.60238C1.33458 2.28497 1.45846 1.98067 1.67869 1.75623C1.89892 1.53179 2.19752 1.40555 2.50897 1.4052H13.0826C13.394 1.40555 13.6926 1.53179 13.9129 1.75623C14.1331 1.98067 14.257 2.28497 14.2573 2.60238V9.01671Z"
                  fill="#9DC540"
                  stroke="#9DC540"
                  stroke-width="0.5"
                />
              </svg>
            ) : (
              ""
            )}
          </div>
        </>
      ),
    },
  ];

  const [columns, setColumns] = useState(getcolumns());

  const onSort = (sortedColumn) => {
    sortingInProgressRef.current = true;
    setColumns(changeTableSortIcons(columns, sortedColumn));
    const columnDirection = getSortDirectionProperty(sortedColumn);
    setDefaultPage(gridState.pageNumber);
    const orderBy =
      columnDirection !== null
        ? `${sortedColumn.id} ${columnDirection}`
        : "id desc";
    setGridState((prevState) => ({
      ...prevState,
      orderBy: orderBy,
    }));
  };

  const onChangePage = (pageNumber) => {
    if (!sortingInProgressRef.current) {
      setGridState((prevState) => ({
        ...prevState,
        pageNumber: pageNumber,
      }));
    }
  };

  const onChangeRowsPerPage = (newPerPage, pageNumber) => {
    setGridState((prevState) => ({
      ...prevState,
      length: newPerPage,
      pageNumber: pageNumber,
    }));
  };

  useEffect(() => {
    if (usersData.pageData !== null) {
      setRowData(usersData.pageData);
    }
    // eslint-disable-next-line
  }, [usersData]);

  useEffect(() => {
    setColumns(getcolumns());
    // eslint-disable-next-line
  }, [i18n.dir()]);

  useEffect(() => {
    dispatch(CourseActions.getCoursesBySystemId(gridState, systemUserId));
    sortingInProgressRef.current = false;
    // eslint-disable-next-line
  }, [modalOpen, gridState]);

  useEffect(() => {
    setGridState((prevState) => ({
      ...prevState,
      search: inputSearch,
    }));
    // eslint-disable-next-line
  }, [inputSearch]);

  const handleSearch = debounce((value) => {
    setInputSearch(value);
  }, process.env.REACT_APP_SEARCH_DEBOUNCE_INTERVAL);

  const handleResetSearch = () => {
    setInputSearch("");
    setGridState((prevState) => ({
      ...prevState,
      search: "",
    }));
    searchInputRef.current.value = "";
  };

  return (
    <>
      <div className="UserDiv">
        <p className="UserTabHeading">{t(translationConstants.MY_COURSES)}</p>
        <div className="tableContainer">
          <Container className="mw-100 p-0 mb-2 mt-3">
            <Row className="mt-2 mb-4">
              <Col
                md="3"
                xs="12"
                className="d-flex justify-content-sm-start justify-content-center"
              >
                <div className="searchInputDiv gap-2 d-flex justify-content-center align-items-center w-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_610_3748)">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.4015 13.3185L16.7053 17.6223C16.8565 17.7737 16.9415 17.979 16.9414 18.193C16.9413 18.407 16.8562 18.6122 16.7049 18.7635C16.5535 18.9147 16.3482 18.9997 16.1342 18.9996C15.9202 18.9995 15.715 18.9144 15.5637 18.7631L11.2599 14.4593C9.97335 15.4558 8.35548 15.9247 6.73543 15.7707C5.11539 15.6167 3.61486 14.8513 2.53911 13.6302C1.46336 12.4091 0.893196 10.8241 0.944603 9.19754C0.99601 7.571 1.66513 6.02514 2.81584 4.87443C3.96655 3.72372 5.51241 3.0546 7.13895 3.0032C8.76549 2.95179 10.3505 3.52196 11.5716 4.59771C12.7927 5.67346 13.5581 7.17398 13.7121 8.79402C13.8661 10.4141 13.3972 12.0319 12.4007 13.3185H12.4015ZM7.3417 14.1993C8.61468 14.1993 9.83553 13.6936 10.7357 12.7934C11.6358 11.8933 12.1415 10.6725 12.1415 9.3995C12.1415 8.12652 11.6358 6.90567 10.7357 6.00554C9.83553 5.10541 8.61468 4.59972 7.3417 4.59972C6.06872 4.59972 4.84788 5.10541 3.94775 6.00554C3.04762 6.90567 2.54193 8.12652 2.54193 9.3995C2.54193 10.6725 3.04762 11.8933 3.94775 12.7934C4.84788 13.6936 6.06872 14.1993 7.3417 14.1993Z"
                        fill="#575756"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_610_3748">
                        <rect
                          width="15.9645"
                          height="15.9998"
                          fill="white"
                          transform="translate(0.941406)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <input
                    className="searchInput w-100"
                    type="text"
                    placeholder={t(translationConstants.Search)}
                    onChange={(e) => handleSearch(e.target.value)}
                    ref={searchInputRef}
                  />
                  {inputSearch && (
                    <div
                      onClick={handleResetSearch}
                      className="cross cursor-pointer"
                    >
                      <img width={16} src={cancelIcon} alt="cancel icon" />
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
          <DataTable
            isShowTimezone={true}
            columns={columns}
            items={rowData}
            onSort={onSort}
            gridState={gridState}
            paginationTotalRows={usersData?.page?.totalElements}
            onChangeRowsPerPage={onChangeRowsPerPage}
            onChangePage={onChangePage}
            paginationDefaultPage={defaultPage}
            paginationResetDefaultPage={true}
          />
          <CustomModal
            size="lg"
            scrollable
            show={isShow}
            setShow={onSetShow}
            customClass="view-user-modal"
          >
            <CourseModal setShow={setIsShow} mode={mode} />
          </CustomModal>

          <CustomModal
            size="lg"
            scrollable
            show={isShowOpenFeedback}
            setShow={onSetFeedbackShow}
            customClass="view-user-modal"
          >
            <FeedbackModal
              setShow={setIsShowOpenFeedback}
              mode={mode}
              courseId={courseId}
              systemUserId={systemUserId}
            />
          </CustomModal>
        </div>
      </div>
    </>
  );
};

export default Courses;
